import { z } from 'zod';

/**
 * Item statuses in display order (PLAN §2.5). `deferred` was removed on 2026-11-03 (D70).
 * `office_action`: the crew's part is done; waiting for the office.
 */
export const RISQ_STATUSES = ['not_started', 'in_progress', 'closed', 'office_action'] as const;

export type RisqStatus = (typeof RISQ_STATUSES)[number];

export const risqStatusSchema = z.enum(RISQ_STATUSES);

/** Icon shown next to the status word. Status is never shown by colour or icon alone. */
export const RISQ_STATUS_ICONS: Readonly<Record<RisqStatus, string>> = {
  not_started: '○',
  in_progress: '◐',
  closed: '✓',
  office_action: '!',
};

/**
 * Done for the crew: Closed, or Office action (the crew's part is done, the office is next).
 * The office sees Office action items separately as "Waiting for office".
 */
export function isDone(status: RisqStatus): boolean {
  return status === 'closed' || status === 'office_action';
}

export interface StatusSummary {
  total: number;
  /** Closed + Office action. */
  done: number;
  open: number;
  /** Office action: done by the crew, waiting for the office. */
  waitingForOffice: number;
  /** Whole percent of done items, rounded down so 99.6% never shows as 100%. */
  percentDone: number;
  byStatus: Record<RisqStatus, number>;
}

export function percentOf(part: number, total: number): number {
  return total === 0 ? 0 : Math.floor((part * 100) / total);
}

export function summarizeStatuses(statuses: Iterable<RisqStatus>): StatusSummary {
  const byStatus: Record<RisqStatus, number> = {
    not_started: 0,
    in_progress: 0,
    closed: 0,
    office_action: 0,
  };
  let total = 0;
  for (const status of statuses) {
    byStatus[status] += 1;
    total += 1;
  }
  const done = byStatus.closed + byStatus.office_action;
  return {
    total,
    done,
    open: total - done,
    waitingForOffice: byStatus.office_action,
    percentDone: percentOf(done, total),
    byStatus,
  };
}
